import { useEffect, useState } from "react";
import { Button, Field, Label, Switch } from "@headlessui/react";
import { isHigherThanDistrictAdmin, UserRole } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import { handleEnterKey } from "../../app/keyboardUtils";
import Select from "../../components/input/Select";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { useGetDistrictsQuery } from "../districts/districtsApiSlice";
import { useGetSchoolsByDistrictIdMutation } from "../districts/districtsApiSlice";
import { useAddUserToSchoolMutation } from "./schoolsApiSlice";

interface Props {
	userId: number;
	schoolIds: number[];
	onClose: () => void;
}

export default function AddExistingSchoolToUser(props: Props) {
	const { role } = useAppSelector((state) => state.user);

	const [selectedDistrict, setSelectedDistrict] = useState<number>();
	const [selectedSchool, setSelectedSchool] = useState<number>();
	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);
	const [includeInactive, setIncludeInactive] = useState(false);

	const getDistrictsResult = useGetDistrictsQuery(role === UserRole.SchoolAdmin);
	const districts = getDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useGetSchoolsByDistrictIdMutation();

	const [addToSchool] = useAddUserToSchoolMutation();

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			if (isHigherThanDistrictAdmin(role!)) {
				options.unshift({ value: 0, label: "None" } as SimpleOption);
			}
			setDistrictOptions(options);

			if (options.length > 0) {
				setSelectedDistrict(Number(options[0].value));
			}
		}
	}, [districts, props.schoolIds]);

	useEffect(() => {
		callGetSchoolsByDistrict({
			districtId: selectedDistrict === 0 ? undefined : selectedDistrict,
			filterInactive: !includeInactive,
		})
			.unwrap()
			.then((schools) => {
				if (schools) {
					const options = schools
						.filter((s) => !props.schoolIds.includes(s.id))
						.map((s) => ({ value: s.id, label: s.name }) as SimpleOption);

					setSchoolOptions(options);

					if (options.length > 0) {
						setSelectedSchool(Number(options[0].value));
					} else {
						setSelectedSchool(undefined);
					}
				}
			});
	}, [selectedDistrict, includeInactive]);

	function handleAddSchool() {
		if (selectedSchool) {
			addToSchool({ schoolId: selectedSchool, userId: props.userId })
				.unwrap()
				.then(() => {
					props.onClose();
				});
		}
	}

	function handleCancel() {
		props.onClose();
	}

	return (
		<section>
			{selectedDistrict !== undefined && districtOptions.length > 1 && (
				<div className="flex gap-x-3 w-full pt-3 items-center">
					<Select
						value={selectedDistrict}
						setValue={(v) => setSelectedDistrict(v as number)}
						simpleListBoxId="districtDropdown"
						options={districtOptions}
						label="District"
						fieldClasses="w-3/4"
						simpleListBoxClasses="w-full"
					/>
					<Field className="w-1/4">
						<Label
							as="span"
							className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
							Include Inactive Schools
						</Label>
						<Switch
							checked={includeInactive}
							onChange={setIncludeInactive}
							className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
							/>
						</Switch>
					</Field>
				</div>
			)}

			<div className="flex gap-x-3 w-full pt-3 items-center">
				{schoolOptions.length > 0 ? (
					<Select
						value={selectedSchool}
						setValue={(v) => setSelectedSchool(v as number)}
						simpleListBoxId="schoolDropdown"
						options={schoolOptions}
						label="School"
						fieldClasses={districtOptions.length > 1 ? "w-full" : "w-3/4"}
						simpleListBoxClasses="w-full"
					/>
				) : (
					<div
						className={`${districtOptions.length > 1 ? "w-full" : "w-3/4"} px-4 py-4 bg-gray-50 border border-gray-200 rounded-lg`}>
						<p className="text-gray-700 text-center">
							There are no available schools to add for the user.
						</p>
					</div>
				)}
				{districtOptions.length <= 1 && (
					<Field className="w-1/4">
						<Label
							as="span"
							className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
							Include Inactive Schools
						</Label>
						<Switch
							checked={includeInactive}
							onChange={setIncludeInactive}
							className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
							/>
						</Switch>
					</Field>
				)}
			</div>

			<div className="mt-6 grid grid-cols-2 gap-3">
				<Button
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					id="confirm-button"
					disabled={!selectedSchool}
					onClick={handleAddSchool}
					onKeyDown={(e) => handleEnterKey(e, handleAddSchool)}
					className="btn btn-primary">
					Add School
				</Button>
			</div>
		</section>
	);
}
